import { useStateContext } from 'context/StateContext'
import { useState } from 'react'
import emailjs from '@emailjs/browser'
import { toast } from 'react-toastify'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore/lite'
import { firestore } from 'config/Firebase'
import { Helmet } from 'react-helmet'

import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined'
import InstagramIcon from '@mui/icons-material/Instagram'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import GitHubIcon from '@mui/icons-material/GitHub'

const initialState = { full_name: '', email: '', subject: '', message: '' }

const contactCards = [
  {
    icon: <PhoneAndroidOutlinedIcon />,
    label: 'Phone',
    value: <a href="tel:+923007588836">+92 300 7588836</a>,
  },
  {
    icon: <EmailOutlinedIcon />,
    label: 'Email',
    value: <a href="mailto:usmanarif2913@gmail.com">usmanarif2913@gmail.com</a>,
  },
  {
    icon: <LocationOnOutlinedIcon />,
    label: 'Location',
    value: (
      <a href="https://maps.app.goo.gl/vfzSc7MuKsdwJodm8" target="_blank" rel="noreferrer">
        Ghulamabad Faisalabad, Pakistan
      </a>
    ),
  },
]

const socialLinks = [
  { href: 'https://www.facebook.com/profile.php?id=100055147804181', icon: <FacebookOutlinedIcon /> },
  { href: 'https://www.instagram.com/usman_arif_13', icon: <InstagramIcon /> },
  { href: 'https://www.linkedin.com/in/muhammad-usman-51a946201', icon: <LinkedInIcon /> },
  { href: 'https://github.com/MuhammadUsmanAnsari', icon: <GitHubIcon /> },
  { href: 'https://wa.me/+923007588836', icon: <WhatsAppIcon /> },
]

export default function Contact() {
  const { handleButtonSound } = useStateContext()
  const [state, setState] = useState(initialState)
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (e) =>
    setState((s) => ({ ...s, [e.target.name]: e.target.value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    const { full_name, email, subject, message } = {
      full_name: state.full_name.trim(),
      email: state.email.trim(),
      subject: state.subject.trim(),
      message: state.message.trim(),
    }
    const mailformat = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/

    if (full_name.length < 5)
      return toast.error('Minimum 5 characters required for name')
    if (!email.match(mailformat))
      return toast.error('Invalid email address')
    if (subject.length < 5)
      return toast.error('Minimum 5 characters required for subject')
    if (message.length < 10)
      return toast.error('Minimum 10 characters required for message')

    const templateParams = { full_name, email, subject, message }
    setIsLoading(true)

    emailjs
      .send('service_yzgdd1o', 'template_667ee4f', templateParams, 'Q2iJU5acHtKdCJIXF')
      .then(() => saveToFirestore({ full_name, email, subject, message }))
      .catch((err) => {
        toast.error(err.message)
        setIsLoading(false)
      })
  }

  const saveToFirestore = async (data) => {
    await setDoc(
      doc(firestore, 'contact-us', data.email),
      { ...data, dateCreated: serverTimestamp() },
      { merge: true }
    )
    setIsLoading(false)
    setState(initialState)
    toast.success('Message sent successfully!')
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Usman Arif | Contact</title>
        <meta name="description" content="Muhammad Usman Arif | Contact Me" />
        <meta name="keywords" content="usmanarif, contact, Muhammad Usman Arif" />
      </Helmet>

      <div id="contact-section" data-aos="fade-right">
        <div className="section-heading">
          <span className="section-tag">Let's Talk</span>
          <h2>Get In <span className="gradient-text">Touch</span></h2>
          <div className="heading-bar" />
        </div>

        <div className="row g-4 mb-4">
          {/* ── Left: Contact Info ── */}
          <div className="col-12 col-lg-5">
            <h4
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: '1.15rem',
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              I'd love to hear from you
            </h4>
            <p
              style={{
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
                marginBottom: '1.5rem',
              }}
            >
              I'm currently available for new projects and open to freelance work.
              Feel free to send a message about anything you'd like to discuss!
            </p>

            {contactCards.map((card, i) => (
              <div className="contact-info-card" key={i}>
                <div className="contact-icon">{card.icon}</div>
                <div className="contact-text">
                  <p className="contact-label">{card.label}</p>
                  <p className="contact-value">{card.value}</p>
                </div>
              </div>
            ))}

            <div className="contact-social-row">
              {socialLinks.map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noreferrer">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div className="col-12 col-lg-7">
            <div className="contact-form-card">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <div className="form-field">
                      <label htmlFor="full_name">Full Name</label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        placeholder="Your full name"
                        value={state.full_name}
                        onChange={handleChange}
                        required
                        minLength={5}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="form-field">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="your@email.com"
                        value={state.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-field">
                      <label htmlFor="subject">Subject</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        placeholder="What's this about?"
                        value={state.subject}
                        onChange={handleChange}
                        required
                        minLength={5}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-field">
                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder="Tell me about your project..."
                        value={state.message}
                        onChange={handleChange}
                        required
                        minLength={10}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-accent px-4"
                      onClick={handleButtonSound}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="d-flex align-items-center gap-2">
                          <span className="spinner-border spinner-border-sm" />
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ── Map ── */}
        <div className="map-container mb-4">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d880.092572020139!2d73.05093068178941!3d31.446436959461117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2s!4v1700586954593!5m2!1sen!2s"
            width="100%"
            height="380"
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Location Map"
          />
        </div>
      </div>
    </>
  )
}
